import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

export function AdminView() {
  const { toast } = useToast();
  const { config, refreshConfig } = useAppConfig();
  const queryClient = useQueryClient();

  // Token Costs State
  const { data: tokenCosts, isLoading: isTokenCostsLoading } = useQuery({
    queryKey: ['token_costs_admin'],
    queryFn: async () => {
      const { data, error } = await supabase.from('token_costs').select('*');
      if (error) throw error;
      return data as { operation: string; cost: number }[];
    },
  });

  const [localTokenCosts, setLocalTokenCosts] = useState<Record<string, number>>({});
  
  useEffect(() => {
    if (tokenCosts) {
      const newCosts: Record<string, number> = {};
      tokenCosts.forEach(tc => newCosts[tc.operation] = tc.cost);
      setLocalTokenCosts(newCosts);
    }
  }, [tokenCosts]);

  // CMS State
  const [tagline, setTagline] = useState(config?.tagline ?? '');
  const [logoUrl, setLogoUrl] = useState(config?.logo_url ?? '');
  const [privacyPolicy, setPrivacyPolicy] = useState(config?.privacy_policy ?? '');
  const [termsOfService, setTermsOfService] = useState(config?.terms_of_service ?? '');
  const [maintenanceMode, setMaintenanceMode] = useState(config?.maintenance_mode ?? false);

  useEffect(() => {
    if (config) {
      setTagline(config.tagline ?? '');
      setLogoUrl(config.logo_url ?? '');
      setPrivacyPolicy(config.privacy_policy ?? '');
      setTermsOfService(config.terms_of_service ?? '');
      setMaintenanceMode(config.maintenance_mode ?? false);
    }
  }, [config]);

  // Mutations
  const updateAppConfig = useMutation({
    mutationFn: async (updates: Partial<typeof config>) => {
      const { error } = await supabase.from('app_config').update(updates as any).eq('id', 1);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'App config updated successfully.', variant: 'default' });
      void refreshConfig();
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  const updateTokenCosts = useMutation({
    mutationFn: async (costs: Record<string, number>) => {
      for (const [operation, cost] of Object.entries(costs)) {
        const { error } = await supabase.from('token_costs').update({ cost }).eq('operation', operation as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Token costs updated successfully.', variant: 'default' });
      void queryClient.invalidateQueries({ queryKey: ['token_costs_admin'] });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  return (
    <div className="flex min-h-screen flex-col items-center bg-adam-bg-secondary-dark p-8 pt-20 text-white">
      <div className="w-full max-w-4xl">
        <h1 className="mb-8 text-3xl font-light">Gienox Operations Center</h1>
        
        <Tabs defaultValue="economics" className="w-full">
          <TabsList className="mb-8 grid w-full grid-cols-4 bg-adam-neutral-900 border-adam-neutral-700">
            <TabsTrigger value="economics" className="data-[state=active]:bg-adam-neutral-100 data-[state=active]:text-adam-neutral-900">Economics</TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-adam-neutral-100 data-[state=active]:text-adam-neutral-900">Branding</TabsTrigger>
            <TabsTrigger value="legal" className="data-[state=active]:bg-adam-neutral-100 data-[state=active]:text-adam-neutral-900">Legal</TabsTrigger>
            <TabsTrigger value="operations" className="data-[state=active]:bg-adam-neutral-100 data-[state=active]:text-adam-neutral-900">Operations</TabsTrigger>
          </TabsList>
          
          <TabsContent value="economics">
            <Card className="border-adam-neutral-700 bg-adam-neutral-900 text-white">
              <CardHeader>
                <CardTitle>Token Costs</CardTitle>
                <CardDescription className="text-adam-neutral-400">Manage how many tokens each AI operation consumes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isTokenCostsLoading ? <Loader2 className="animate-spin text-adam-primary" /> : (
                  Object.entries(localTokenCosts).map(([operation, cost]) => (
                    <div key={operation} className="flex flex-col space-y-1.5">
                      <Label htmlFor={operation} className="capitalize">{operation} Cost</Label>
                      <Input 
                        id={operation} 
                        type="number" 
                        className="bg-adam-neutral-800 border-adam-neutral-700 text-white" 
                        value={cost} 
                        onChange={(e) => setLocalTokenCosts(prev => ({ ...prev, [operation]: parseInt(e.target.value) || 0 }))} 
                      />
                    </div>
                  ))
                )}
              </CardContent>
              <CardFooter>
                <Button variant="dark" disabled={updateTokenCosts.isPending} onClick={() => updateTokenCosts.mutate(localTokenCosts)}>
                  {updateTokenCosts.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Prices
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="branding">
            <Card className="border-adam-neutral-700 bg-adam-neutral-900 text-white">
              <CardHeader>
                <CardTitle>Branding & Content</CardTitle>
                <CardDescription className="text-adam-neutral-400">Update the tagline and logo shown across the app.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input 
                    id="tagline" 
                    className="bg-adam-neutral-800 border-adam-neutral-700 text-white" 
                    value={tagline} 
                    onChange={(e) => setTagline(e.target.value)} 
                  />
                </div>
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="logoUrl">Logo Image URL</Label>
                  <Input 
                    id="logoUrl" 
                    className="bg-adam-neutral-800 border-adam-neutral-700 text-white" 
                    value={logoUrl} 
                    onChange={(e) => setLogoUrl(e.target.value)} 
                    placeholder="https://example.com/logo.png"
                  />
                  {logoUrl && <img src={logoUrl} alt="Logo Preview" className="mt-2 max-h-12 w-auto rounded border border-adam-neutral-700 object-contain p-1" />}
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="dark" disabled={updateAppConfig.isPending} onClick={() => updateAppConfig.mutate({ tagline, logo_url: logoUrl })}>
                  {updateAppConfig.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Branding
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="legal">
            <Card className="border-adam-neutral-700 bg-adam-neutral-900 text-white">
              <CardHeader>
                <CardTitle>Legal Documents</CardTitle>
                <CardDescription className="text-adam-neutral-400">Manage your Terms of Service and Privacy Policy (Markdown supported).</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="tos">Terms of Service</Label>
                  <Textarea 
                    id="tos" 
                    className="min-h-[200px] bg-adam-neutral-800 border-adam-neutral-700 text-white font-mono text-xs" 
                    value={termsOfService} 
                    onChange={(e) => setTermsOfService(e.target.value)} 
                  />
                </div>
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="privacy">Privacy Policy</Label>
                  <Textarea 
                    id="privacy" 
                    className="min-h-[200px] bg-adam-neutral-800 border-adam-neutral-700 text-white font-mono text-xs" 
                    value={privacyPolicy} 
                    onChange={(e) => setPrivacyPolicy(e.target.value)} 
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="dark" disabled={updateAppConfig.isPending} onClick={() => updateAppConfig.mutate({ privacy_policy: privacyPolicy, terms_of_service: termsOfService })}>
                  {updateAppConfig.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Legal Docs
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="operations">
            <Card className="border-red-900/50 bg-adam-neutral-900 text-white">
              <CardHeader>
                <CardTitle className="text-red-500">Danger Zone</CardTitle>
                <CardDescription className="text-adam-neutral-400">Critical operational controls.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-row items-center justify-between rounded-lg border border-red-900/30 bg-red-950/20 p-4">
                  <div className="space-y-0.5">
                    <Label className="text-base text-red-400">Maintenance Mode</Label>
                    <p className="text-sm text-adam-neutral-400">
                      Enable this during massive database migrations. Users will be locked out immediately.
                    </p>
                  </div>
                  <Switch
                    checked={maintenanceMode}
                    onCheckedChange={(checked) => {
                      setMaintenanceMode(checked);
                      updateAppConfig.mutate({ maintenance_mode: checked });
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
